import * as z from "zod";
import { isUniqueConstraintViolation } from "../dbErrors";
import { userFacingError } from "../authorization";
import { creatorUserId } from "../rbac/roles";
import { insertGrantsDeduped } from "../rbac/validation";
import { isPublicHttpsUrl } from "../validateExternalUrl";
import { mutator } from "./mutator";

export const registerOidcProviderSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  issuer: z.string().min(1).max(2048),
  audience: z.string().min(1).max(2048),
  jwksUri: z.string().max(2048).optional(),
  // When set, grant this service account manage rights on the new provider so it
  // can maintain it (e.g. re-point the issuer on a domain change) without holding
  // the tenant-wide oidc_providers_manage permission.
  managerServiceAccountId: z.string().optional(),
});

// Registering an OIDC provider is the tenant-level trust decision: it declares
// an issuer whose signed tokens may be exchanged for service-account
// credentials. Gated on tenant:oidc_providers_manage (held by tenant admins via
// allow_all). Service accounts then reference a registered provider; they
// cannot introduce a new trusted issuer themselves.
export const registerOidcProvider = mutator({
  schema: registerOidcProviderSchema,
  resolve: {
    // Resolved in-tenant so a typo'd or cross-tenant id can't create a dead
    // (or FK-violating) grant; only present when managerServiceAccountId is set.
    managerServiceAccount: {
      kind: "serviceAccount",
      from: "managerServiceAccountId",
      optional: true,
    },
  },
  permissions: [
    {
      permission: "tenant:oidc_providers_manage",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      id,
      name,
      issuer,
      audience,
      jwksUri,
      managerServiceAccountId,
      managerServiceAccount,
    },
  }) => {
    if (!isPublicHttpsUrl(issuer)) {
      throw userFacingError("Issuer must be a public HTTPS URL");
    }
    if (jwksUri && !isPublicHttpsUrl(jwksUri)) {
      throw userFacingError("JWKS URL must be a public HTTPS URL");
    }
    const now = Date.now();
    try {
      await tx.mutate.oidcProviders.insert({
        id,
        name,
        issuer,
        audience,
        jwksUri: jwksUri ?? null,
        disabled: false,
        createdByUserId: creatorUserId(ctx),
        createdAt: now,
        updatedAt: now,
        tenantId: ctx.tenantId,
      });
    } catch (err) {
      if (isUniqueConstraintViolation(err, "oidc_providers_issuer_unique")) {
        throw userFacingError(
          "A provider with this issuer is already registered"
        );
      }
      throw err;
    }

    if (managerServiceAccountId) {
      if (!managerServiceAccount) {
        throw userFacingError("Manager service account not found");
      }
      await insertGrantsDeduped(
        tx,
        [
          {
            serviceAccountId: managerServiceAccount.id,
            builtinRoleId: "oidc_provider_manager",
          },
        ],
        id,
        ctx.tenantId
      );
    }
  },
});
